import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Eye, Calendar, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { PlatformIcon } from '../../utils/platformIcons';
import { useSocialMediaOptions } from '../../utils/socialMediaOptions';
import { resolveBadgeStyle } from '../../utils/badgeColors';
import UserAvatar from '../UserAvatar';
import { LinkPillList } from '../LinkPill';
import PostTimeline from './PostTimeline';
import PostComments from './PostComments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Standardized Post Detail Modal Component
 * Used across Social Media Posts, Calendar View, and Account & College Tracker
 */
const PostDetailModal = ({ 
  isOpen, 
  onClose, 
  post, 
  users = [], 
  canModify = false, 
  onEdit = null,
  getUserName = (id) => id 
}) => {
  const { PLATFORM_ICONS, ACCOUNT_COLORS, COLLEGE_COLORS, CONTENT_TYPE_COLORS } = useSocialMediaOptions();
  const [activeTab, setActiveTab] = useState('overview');
  const [commentCount, setCommentCount] = useState(null);

  // Reset to Overview + pre-fetch the comment count whenever a new post is opened.
  useEffect(() => {
    setActiveTab('overview');
    setCommentCount(null);
    if (isOpen && post?.id) {
      const token = (() => { try { return localStorage.getItem('token'); } catch (_) { return null; } })();
      axios.get(`${API}/social-media/posts/${post.id}/comments`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => setCommentCount(res.data?.count ?? (res.data?.comments?.length || 0)))
        .catch(() => {});
    }
  }, [isOpen, post?.id]);

  if (!post) return null;

  // Status color mapping
  const getStatusColor = (status) => {
    const colors = {
      'Published': 'bg-lime-100 text-lime-700',
      'Completed': 'bg-lime-100 text-lime-700',
      'Scheduled': 'bg-amber-100 text-amber-700',
      'Out Delivered': 'bg-teal-100 text-teal-700',
      'In Shoot': 'bg-blue-100 text-blue-700',
      'In Editing': 'bg-purple-100 text-purple-700',
      'In Corrections': 'bg-orange-100 text-orange-700',
      'Yet to Shoot': 'bg-slate-100 text-slate-600',
      'Pending': 'bg-slate-100 text-slate-600',
      'Not Approved': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  // Find assigned user
  const assignedUser = users.find(u => u.id === post.assigned_to);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Eye className="text-cyan-600" size={24} />
            Post Details
          </DialogTitle>
        </DialogHeader>

        {/* Tabs — Overview hosts the existing card layout, Timeline shows the
            activity feed, Comments hosts the discussion thread. */}
        <div className="border-b border-slate-200 -mt-2">
          <nav className="flex gap-4 px-1" role="tablist" data-testid="post-modal-tabs">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'timeline', label: 'Timeline' },
              { id: 'comments', label: commentCount === null ? 'Comments' : `Comments (${commentCount})` },
            ].map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`post-modal-tab-${t.id}`}
                  className={`relative px-1 py-2.5 text-sm font-medium transition-colors ${
                    active ? 'text-cyan-600' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                  {active && (
                    <span className="absolute inset-x-0 -bottom-px h-0.5 bg-cyan-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {activeTab === 'timeline' && (
          <div className="pt-4" data-testid="post-modal-timeline-tab">
            <PostTimeline postId={post.id} users={users} />
          </div>
        )}
        {activeTab === 'comments' && (
          <div className="pt-4 pb-2" data-testid="post-modal-comments-tab">
            <PostComments postId={post.id} onCountChange={setCommentCount} />
          </div>
        )}

        {activeTab === 'overview' && (
        <div className="space-y-6 py-4">
          {/* Content Name / Title */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-100">
            <h3 className="text-lg font-bold text-slate-900 mb-1">{post.content_name}</h3>
            {post.host_by && (
              <p className="text-sm text-slate-600">Hosted by: {post.host_by}</p>
            )}
          </div>

          {/* Main Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Account */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Account</p>
              {post.account ? (() => {
                const badge = resolveBadgeStyle(post.account, ACCOUNT_COLORS);
                return (
                  <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${badge.className}`} style={badge.style} data-testid="post-detail-account-badge">
                    {post.account}
                  </span>
                );
              })() : <p className="text-sm font-medium text-slate-900">-</p>}
            </div>

            {/* College */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">College</p>
              {(() => {
                const collegeLabel = post.college || 'NA';
                const badge = resolveBadgeStyle(collegeLabel, COLLEGE_COLORS);
                return (
                  <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${badge.className}`} style={badge.style} data-testid="post-detail-college-badge">
                    {collegeLabel}
                  </span>
                );
              })()}
            </div>

            {/* Content Type */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Content Type</p>
              {post.content_type ? (() => {
                const badge = resolveBadgeStyle(post.content_type, CONTENT_TYPE_COLORS);
                return (
                  <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md ${badge.className}`} style={badge.style} data-testid="post-detail-content-type-badge">
                    {post.content_type}
                  </span>
                );
              })() : <p className="text-sm font-medium text-slate-900">-</p>}
            </div>

            {/* Language */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Language</p>
              <p className="text-sm font-medium text-slate-900">{post.language || '-'}</p>
            </div>

            {/* Status */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Status</p>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(post.status)}`}>
                {post.status}
              </span>
            </div>

            {/* Approved for Posting */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Approved</p>
              <span className={`px-2 py-1 rounded text-xs font-semibold ${post.approved_for_posting ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                {post.approved_for_posting ? 'Yes' : 'No'}
              </span>
            </div>
          </div>

          {/* Platforms */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Platforms</p>
            <div className="flex flex-wrap gap-2">
              {(post.platforms || []).length > 0 ? (
                post.platforms.map(p => (
                  <span key={p} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700">
                    <PlatformIcon platform={p} size={16} iconMap={PLATFORM_ICONS} />
                    {p}
                  </span>
                ))
              ) : (
                <span className="text-slate-400 text-sm">No platforms specified</span>
              )}
            </div>
          </div>

          {/* Dates Section */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Important Dates</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500 mb-1">Content Received</p>
                <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                  <Calendar size={14} className="text-cyan-600" />
                  {post.content_received_date 
                    ? format(new Date(post.content_received_date), 'MMM dd, yyyy')
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Scheduled Date</p>
                <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                  <Calendar size={14} className="text-blue-600" />
                  {post.scheduled_date 
                    ? format(new Date(post.scheduled_date), 'MMM dd, yyyy')
                    : '-'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Posted Date</p>
                <p className="text-sm font-medium text-slate-900 flex items-center gap-1">
                  <Calendar size={14} className="text-green-600" />
                  {post.posted_date 
                    ? format(new Date(post.posted_date), 'MMM dd, yyyy')
                    : '-'}
                </p>
              </div>
            </div>
          </div>

          {/* Assigned User */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Assigned To</p>
            {post.assigned_to ? (
              <div className="flex items-center gap-2">
                <UserAvatar user={assignedUser} size="md" />
                <span className="text-sm font-medium text-slate-900">
                  {getUserName(post.assigned_to)}
                </span>
              </div>
            ) : (
              <span className="text-slate-400 text-sm italic">Unassigned</span>
            )}
          </div>

          {/* Notes / Description */}
          {post.notes && (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Notes / Description</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{post.notes}</p>
            </div>
          )}

          {/* Links / URLs — shared LinkPillList component */}
          {post.links && post.links.length > 0 && (
            <div data-testid="post-detail-links">
              <LinkPillList links={post.links} testIdPrefix="post-detail-link" />
            </div>
          )}

          {/* Created/Updated Info */}
          <div className="bg-slate-100 rounded-lg p-4 border border-slate-200">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-500 mb-1">Created</p>
                <p className="font-medium text-slate-700">
                  {post.created_at 
                    ? format(new Date(post.created_at), 'MMM dd, yyyy h:mm a')
                    : 'N/A'}
                </p>
                <p className="text-slate-500 text-xs">by {getUserName(post.created_by)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Last Updated</p>
                <p className="font-medium text-slate-700">
                  {post.updated_at 
                    ? format(new Date(post.updated_at), 'MMM dd, yyyy h:mm a')
                    : 'N/A'}
                </p>
                <p className="text-slate-500 text-xs">by {getUserName(post.updated_by || post.created_by)}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Action Buttons — visible on every tab */}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
          <Button variant="outline" onClick={() => onClose(false)} data-testid="post-detail-close">
            Close
          </Button>
          {canModify && onEdit && (
            <Button onClick={onEdit} className="bg-cyan-600 hover:bg-cyan-700" data-testid="post-detail-edit">
              <Edit size={16} className="mr-2" />
              Edit Post
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostDetailModal;
